import React, { useState } from 'react';
import { submitQuoteRequest } from '../services/firebaseService';

const QuoteRequestSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    giftType: '',
    quantity: '',
    budget: '',
    timeline: '',
    description: '',
    occasion: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Map form data to match Firebase service expectations
      const mappedData = {
        customerName: formData.name,
        companyName: formData.company,
        email: formData.email,
        phone: formData.phone,
        category: formData.giftType,
        quantity: formData.quantity,
        budget: formData.budget,
        deliveryDate: formData.timeline,
        requirements: formData.description,
        occasion: formData.occasion
      };

      const result = await submitQuoteRequest(mappedData);
      
      if (result.success) {
        alert('Thank you for your quote request! Our team will contact you within 24 hours with a detailed proposal.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          giftType: '',
          quantity: '',
          budget: '',
          timeline: '',
          description: '',
          occasion: ''
        });
      } else {
        alert('There was an error submitting your quote request. Please try again or contact us directly.');
        console.error('Quote request error:', result.error);
      }
    } catch (error) {
      alert('There was an error submitting your quote request. Please try again or contact us directly.');
      console.error('Quote submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 bg-gradient-to-br from-cream-50 to-burlap-50 px-4">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-10">
          <span className="text-sm font-bold text-primary-600 uppercase tracking-wider bg-primary-100 px-4 py-2 rounded-full mb-4 inline-block">
            Custom Orders
          </span>
          <h2 className="text-3xl sm:text-4xl font-display font-bold text-burlap-800 mb-4">
            Request a <span className="gradient-gold">Custom Quote</span>
          </h2>
          <p className="text-lg text-burlap-600 max-w-2xl mx-auto">
            Looking for something special? Let us create a personalized solution for your gifting needs
          </p>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 lg:p-8 border border-cream-200">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 placeholder-gray-400 bg-white/90"
                placeholder="Full Name *"
              />
              
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 placeholder-gray-400 bg-white/90"
                placeholder="Email Address *"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 placeholder-gray-400 bg-white/90"
                placeholder="Phone Number"
              />
              
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 placeholder-gray-400 bg-white/90"
                placeholder="Company/Organization"
              />
            </div>

            {/* Project Details */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <select
                name="giftType"
                value={formData.giftType}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 bg-white/90 appearance-none"
              >
                <option value="">Gift Type *</option>
                <option value="corporate">Corporate Gifts</option>
                <option value="traditional">Traditional Crafts</option>
                <option value="handmade">Handmade Items</option>
                <option value="luxury">Luxury Collection</option>
                <option value="wedding">Wedding Gifts</option>
                <option value="custom">Custom Design</option>
              </select>
              
              <select
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 bg-white/90 appearance-none"
              >
                <option value="">Quantity</option>
                <option value="1-10">1-10 pieces</option>
                <option value="11-50">11-50 pieces</option>
                <option value="51-100">51-100 pieces</option>
                <option value="101-500">101-500 pieces</option>
                <option value="500+">500+ pieces</option>
              </select>
              
              <select
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 bg-white/90 appearance-none"
              >
                <option value="">Budget Range</option>
                <option value="under-40000">Under ₹40,000</option>
                <option value="40000-80000">₹40,000 - ₹80,000</option>
                <option value="80000-400000">₹80,000 - ₹4,00,000</option>
                <option value="400000-800000">₹4,00,000 - ₹8,00,000</option>
                <option value="over-800000">Over ₹8,00,000</option>
              </select>
              
              <select
                name="timeline"
                value={formData.timeline}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 bg-white/90 appearance-none"
              >
                <option value="">Timeline</option>
                <option value="rush">Rush (1-2 weeks)</option>
                <option value="standard">Standard (3-4 weeks)</option>
                <option value="extended">Extended (5-8 weeks)</option>
                <option value="flexible">Flexible timeline</option>
              </select>
            </div>

            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows="4"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 resize-none placeholder-gray-400 bg-white/90"
              placeholder="Describe your vision, requirements, customization needs, or any specific details *"
            ></textarea>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 px-6 rounded-xl font-semibold text-white transition-all duration-300 ${
                isSubmitting 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 transform hover:scale-[1.02] shadow-lg hover:shadow-xl'
              }`}
            >
              {isSubmitting ? 'Submitting Request...' : 'Request Custom Quote'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default QuoteRequestSection;
